import React, { useEffect, useState } from "react";
import { JERICHO_TOKEN_KEY } from "./JerichoEasterEgg";

/**
 * JerichoOperatorBar — sticky header inside /jericho showing the active
 * operator, session expiry, IP-derived location, and the "Seal Node"
 * (logout) + optional precise-location consent controls.
 *
 * Also runs a 60s heartbeat so the backend keeps the session "active"
 * in the admin live console.
 */
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const HEARTBEAT_MS = 60_000;

function _fmtCountdown(expiresAt) {
  if (!expiresAt) return "—";
  const ms = new Date(expiresAt).getTime() - Date.now();
  if (ms <= 0) return "00:00";
  const m = Math.floor(ms / 60000);
  const s = Math.floor((ms % 60000) / 1000);
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

export const JerichoOperatorBar = ({ operator }) => {
  const [countdown, setCountdown] = useState(_fmtCountdown(operator?.expires_at));
  const [preciseStatus, setPreciseStatus] = useState(
    operator?.precise_location ? "consented" : "idle",
  );

  // Heartbeat
  useEffect(() => {
    let stop = false;
    const ping = async () => {
      const token = (() => {
        try { return sessionStorage.getItem(JERICHO_TOKEN_KEY); }
        catch (_) { return null; }
      })();
      if (!token) return;
      try {
        await fetch(`${API}/jericho/heartbeat`, {
          method: "POST",
          headers: { "x-jericho-token": token },
        });
      } catch (_) { /* ignore */ }
    };
    ping();
    const id = window.setInterval(() => { if (!stop) ping(); }, HEARTBEAT_MS);
    return () => { stop = true; window.clearInterval(id); };
  }, []);

  // Countdown ticker
  useEffect(() => {
    if (!operator?.expires_at) return undefined;
    const id = window.setInterval(() => {
      setCountdown(_fmtCountdown(operator.expires_at));
    }, 1000);
    return () => window.clearInterval(id);
  }, [operator?.expires_at]);

  const seal = async () => {
    const token = (() => {
      try { return sessionStorage.getItem(JERICHO_TOKEN_KEY); }
      catch (_) { return null; }
    })();
    try {
      if (token) {
        await fetch(`${API}/jericho/seal`, {
          method: "POST",
          headers: { "x-jericho-token": token },
        });
      }
    } catch (_) { /* ignore */ }
    try { sessionStorage.removeItem(JERICHO_TOKEN_KEY); }
    catch (_) { /* noop */ }
    window.location.replace("/");
  };

  const consentPrecise = () => {
    if (!("geolocation" in navigator)) {
      setPreciseStatus("unsupported");
      return;
    }
    setPreciseStatus("requesting");
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const token = (() => {
          try { return sessionStorage.getItem(JERICHO_TOKEN_KEY); }
          catch (_) { return null; }
        })();
        if (!token) { setPreciseStatus("idle"); return; }
        try {
          const res = await fetch(`${API}/jericho/precise-location`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "x-jericho-token": token,
            },
            body: JSON.stringify({
              lat: pos.coords.latitude,
              lon: pos.coords.longitude,
              accuracy_m: pos.coords.accuracy,
            }),
          });
          setPreciseStatus(res.ok ? "consented" : "declined");
        } catch (_) {
          setPreciseStatus("declined");
        }
      },
      () => setPreciseStatus("declined"),
      { enableHighAccuracy: true, timeout: 8000, maximumAge: 0 },
    );
  };

  if (!operator) return null;

  const loc = operator.ip_location || {};
  const locLabel = [loc.city, loc.region, loc.country].filter(Boolean).join(" · ") || "—";

  return (
    <div
      data-testid="jericho-operator-bar"
      className="sticky top-0 z-30 bg-[#0a0907]/95 backdrop-blur-sm border-b border-[#D92D20]/40"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-8 py-2 flex flex-wrap items-center gap-x-5 gap-y-1 font-mono text-[10px] uppercase tracking-[0.3em] text-[#A39E93]">
        <span className="flex items-center gap-2 text-[#5DD4B3]">
          <span className="block w-1.5 h-1.5 rounded-full bg-[#5DD4B3] animate-pulse" />
          NODE JERICHO · ACTIVE
        </span>
        <span data-testid="jericho-callsign" className="text-[#E4DFD3]">
          OPERATOR: {operator.callsign || "—"}
        </span>
        <span>ROLE: {operator.role}</span>
        <span>LOC: {locLabel}</span>
        <span>DEVICE: {operator.device}/{operator.os}</span>
        <span>
          EXPIRES IN:{" "}
          <span data-testid="jericho-expires-countdown" className="text-[#E0A526]">
            {countdown}
          </span>
        </span>
        <div className="ml-auto flex items-center gap-3">
          {preciseStatus === "idle" && (
            <button
              type="button"
              onClick={consentPrecise}
              data-testid="jericho-consent-precise"
              className="text-[#7a8294] hover:text-[#5DD4B3] underline"
            >
              + enable precise location
            </button>
          )}
          {preciseStatus === "requesting" && (
            <span className="text-[#7a8294]">// requesting…</span>
          )}
          {preciseStatus === "consented" && (
            <span data-testid="jericho-precise-on" className="text-[#5DD4B3]">
              ⌖ PRECISE LOC ON
            </span>
          )}
          {preciseStatus === "declined" && (
            <span className="text-[#7a8294]">// precise loc declined</span>
          )}
          <button
            type="button"
            onClick={seal}
            data-testid="jericho-seal-node"
            className="px-3 py-1 border border-[#D92D20]/60 bg-[#D92D20]/10 hover:bg-[#D92D20]/20 text-[#D92D20] tracking-[0.35em]"
          >
            SEAL NODE
          </button>
        </div>
      </div>
    </div>
  );
};

export default JerichoOperatorBar;
